import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, NavLink } from 'react-router-dom';
import './index.css';
import { signOut } from 'firebase/auth';
import { auth } from './firebase-config';
import { doc, getDoc } from 'firebase/firestore';

import Blog from './components/Blog';
import SignIn from './components/SignIn';
import SignUp from './components/SignUp';
import VerifyEmail from './components/VerifyEmail';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import Profile from './components/Profile';
import Home from './components/Home';
import ProblemSet from './components/ProblemSet';
import Help from './components/Help';
import Contests from './components/Contests';
import Ratings from './components/Ratings';
import ProblemPage from './components/ProblemPage';
import RatingLeaderboard from './components/RatingLeaderboard';
import ContestLeaderboard from './components/ContestLeaderboard';
import { useAuthUser } from './components/useAuthUser';
import EElogo from "./assets/EE_Logo.png"

import 'katex/dist/katex.min.css';
import { db } from './firebase-config';

const App = () =>
{
  const { userAuth, loading } = useAuthUser();
  const navigate = useNavigate();
  const [loadingStandard, setLoadingStandard] = useState(true);
  const [standard, setStandard] = useState(0);

  useEffect(() =>
  {
    if (!loading && userAuth)
    {
      const getStandard = async () =>
      {
        setLoadingStandard(true);
        try
        {
          const username = userAuth.displayName;
          const userRef = doc(db, "users", username);
          const snapshot = await getDoc(userRef);

          if (snapshot.exists())
          {
            setStandard(snapshot.data().Standard);
          }
          else
          {
            setStandard(0);
          }
        }
        catch (error)
        {
          console.error(error);
          setStandard(0);
        }
        finally
        {
          setLoadingStandard(false);
        }
      };

      getStandard();
    }
  }, [loading, userAuth]);

  const handleSignOut = async () =>
  {
    try
    {
      await signOut(auth);
      navigate('/signin');
    }
    catch (err)
    {
      console.error(err);
    }
  };

  if (loading)
  {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400 text-lg sm:text-2xl font-medium tracking-wide animate-pulse">
        Connecting to EduEndurance...
      </div>
    );
  }

  if (!userAuth)
  {
    return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="*" element={<Navigate to="/signin" replace />} />
      </Routes>
    );
  }

  const username = userAuth.displayName;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100 font-sans selection:bg-indigo-500 selection:text-white transition-colors duration-300">

      {/* HEADER */}
      <header className="mx-2 sm:mx-6 mt-4 p-3 sm:p-5 bg-white/70 dark:bg-slate-900/70 backdrop-blur-md rounded-3xl border border-slate-300 dark:border-slate-700 shadow-sm">
        <div className="container mx-auto flex justify-between items-center rounded-2xl bg-white dark:bg-slate-800/80 border border-slate-300 dark:border-slate-600 shadow-sm px-4 sm:px-6 py-3">
          
          {/* LEFT: Logo + Name */}
          <div className="flex items-center space-x-3 sm:space-x-5">
            <img
              src={EElogo}
              alt="EE Logo"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain rounded-xl drop-shadow-sm"
            />
            <span className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400">
              EduEndurance
            </span>
          </div>

          {/* RIGHT: Username + Sign Out */}
          <div className="flex items-center space-x-4 sm:space-x-6">
            <Link
              to={`/profile/${username}`}
              className="hidden sm:inline font-semibold text-sm lg:text-base text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors truncate max-w-[120px] sm:max-w-none"
            >
              {username}
            </Link>

            <button
              onClick={handleSignOut}
              className="text-xs sm:text-sm font-semibold px-4 py-2 rounded-full border border-rose-300 dark:border-rose-700 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-all duration-300 active:scale-95"
            >
              Sign Out
            </button>
          </div>

        </div>

        {/* NAVBAR */}
        <nav className="container mx-auto mt-4 flex flex-wrap gap-1 sm:gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/50 rounded-2xl justify-center sm:justify-start border border-slate-300 dark:border-slate-600">
          {[
            { path: '/', label: 'home' },
            { path: '/contests', label: 'contests' },
            { path: '/problemset/p1', label: 'problemset' },
            { path: '/blogs', label: 'blogs' },
            { path: '/ratings', label: 'ratings' },
            { path: '/help', label: 'help' },
            { path: `/profile/${username}`, label: 'profile' },
          ].map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              end={path === "/"}
              className={({ isActive }) =>
                `
                  py-2 px-4 sm:px-5 rounded-xl font-medium capitalize text-black text-sm sm:text-base
                  transition-all duration-300
                  ${isActive
                    ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow-sm ring-1 ring-slate-200 dark:ring-slate-500"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/50 dark:hover:bg-slate-700/50"}
                `
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* MAIN */}
      <main className="flex-1 p-2 mt-2 mb-2 bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-300 dark:border-slate-700 mx-2 sm:mx-6">
        <Routes>
          <Route path="/" element={<ProtectedRoute> <Home /> </ProtectedRoute>} />
          <Route path="/contests" element={<ProtectedRoute> <Contests /> </ProtectedRoute>} />
          <Route path="/problemset/:page" element={<ProtectedRoute> <ProblemSet Std={standard} /> </ProtectedRoute>} />
          <Route path="/problemset" element={<Navigate to="/problemset/p1" replace />} />
          <Route path="/problems/:problemId" element={<ProtectedRoute> <ProblemPage Std={standard} /> </ProtectedRoute>} />
          <Route path="/blogs" element={<ProtectedRoute> <Blog /> </ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute> <Help /> </ProtectedRoute>} />
          <Route path="/profile/:username" element={<ProtectedRoute> <Profile Std={standard} /> </ProtectedRoute>} />
          <Route path="/ratings" element={<ProtectedRoute> <Ratings /> </ProtectedRoute>} />
          <Route path="/leaderboard_rating/standard/:Std/:page" element={<ProtectedRoute> <RatingLeaderboard /> </ProtectedRoute>} />
          <Route path="/contest_leaderboard/:contestId/:page" element={<ProtectedRoute> <ContestLeaderboard /> </ProtectedRoute>} />
          <Route path="*" element={<ProtectedRoute> <Navigate to="/" replace /> </ProtectedRoute>} />
        </Routes>
      </main>

      {/* FOOTER */}
      <footer className="mt-auto py-6 sm:py-8 text-center bg-transparent">
        <div className="container mx-auto flex flex-col items-center justify-center space-y-1">
          <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 font-medium">
            &copy; {new Date().getFullYear()} EduEndurance. All rights reserved.
          </p>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-500 tracking-wide">
            Made with efforts by TanX
          </p>
        </div>
      </footer>

    </div>
  );
};

export default App;